import classnames from 'classnames2';
import React from 'react';

import { SelectWidths } from '../types';
import { useSelectContext } from '../useSelectContext';

import styles from './Select.List.module.scss';

export interface ISelectListProps {
  width?: SelectWidths;
  hasFooter?: boolean;
  height?: number | string;
  onBlur?(event: React.FocusEvent): void;
}

export const SelectList: React.FC<ISelectListProps> = ({ hasFooter, width, height, ...props }) => {
  const {
    popper,
    focusedIndex,
    setFocusedIndex,
    isOpen,
    onOpen,
    onClose,
    focusableItems,
    menuId,
    buttonId,
    hasParentMenu,
    reference,
    menuRef
  } = useSelectContext();

  const handleKeyDown = (event) => {
    const count = focusableItems.current.length;
    let nextIndex;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      // tslint:disable-next-line
      nextIndex = (focusedIndex + 1) % count;
      setFocusedIndex(nextIndex);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      // tslint:disable-next-line
      nextIndex = (focusedIndex - 1 + count) % count;
      setFocusedIndex(nextIndex);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      event.stopPropagation();
      if (hasParentMenu) {
        onClose();
        if (reference.ref.current) {
          reference.ref.current.focus();
        }
      }
    } else if (event.key === 'Tab') {
      if (event.shiftKey) {
        event.preventDefault();
        onClose();
      }
    } else if (event.key === 'Escape') {
      onClose();
    }
  };

  const onMouseEnter = () => {
    // If we're in a nested menu, keep the menu open when we mouse into it
    if (hasParentMenu) {
      onOpen();
    }
  };

  const onMouseLeave = (e) => {
    if (hasParentMenu) {
      onClose();
    }
  };

  const menuWidthStyle = () => {
    switch (width) {
      case 'full':
        return styles.fullContainerWidth;
      default:
        return styles.defaultMenuWidth;
    }
  };

  const handleBlur = (event: React.FocusEvent) => {
    const target = (event.relatedTarget || document.activeElement) as HTMLElement;
    let isWithinSelf = false;

    if (popper.ref.current) {
      isWithinSelf = popper.ref.current.contains(target);
    }

    if (!isWithinSelf && !hasParentMenu) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      role="listbox"
      className={classnames(styles.menu, menuWidthStyle(), hasFooter && styles.withFooter)}
      ref={menuRef}
      id={menuId}
      aria-labelledby={buttonId}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={handleKeyDown}
      onBlur={props.onBlur ? props.onBlur : handleBlur}
      tabIndex={-1}
      style={{ ...popper.style, ...{ maxHeight: height } }}
    >
      {props.children}
    </div>
  );
};

SelectList.displayName = 'Select.List';
